import { useQuery } from '@tanstack/react-query';
import { DEFAULT_UNIT_PREFERENCES } from '@oip/contracts';
import type { UnitPreferences, UnitSources } from '@oip/contracts';
import { endpoints } from '../api/endpoints';
import { useTenantKey } from '../hooks/api';

/**
 * Metric, from the contract — the same constant the server falls back to.
 *
 * This was restated locally while `@oip/contracts` compiled to CommonJS and the
 * web app could not import its runtime values (R9). Sharing it again removes
 * the chance of the two defaults drifting apart.
 */
export const DEFAULT_UNITS = DEFAULT_UNIT_PREFERENCES;

/**
 * The tenant's display units.
 *
 * Read from its own endpoint rather than from `useAuth`, because units are
 * tenant configuration that changes without the session changing — a setting
 * saved in another tab must reach this one without a re-login.
 *
 * Long `staleTime`: this is configuration, not data. Refetching it on every
 * window focus would issue a request per page visit to answer a question whose
 * answer changes perhaps twice a year. The settings page invalidates the key
 * on save, so a change is still immediate.
 */
function useUnitsQuery() {
  const tenantKey = useTenantKey();
  return useQuery({
    queryKey: ['tenant-units', tenantKey],
    queryFn: () => endpoints.units(),
    staleTime: 60 * 60_000,
    // Never leaves the UI without units: an unreadable setting must not blank
    // every number on the page.
    retry: false,
  });
}

export function useUnits(): UnitPreferences {
  return useUnitsQuery().data?.units ?? DEFAULT_UNITS;
}

/**
 * The units AND where each came from.
 *
 * A second hook rather than widening `useUnits`, whose forty-odd callers want a
 * number formatted and nothing else. Only the settings screen asks where a value
 * came from, and only it should pay for knowing.
 *
 * `sources` is absent while the query is in flight — not defaulted to `TENANT`,
 * which would flash "you chose this" under a value the tenant may not have
 * chosen at all.
 */
export function useUnitSources(): {
  sources: UnitSources | null;
  partnerName: string | null;
} {
  const { data } = useUnitsQuery();
  return { sources: data?.sources ?? null, partnerName: data?.partnerName ?? null };
}
